import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoutButton } from './Login';
import { useAuth } from '../context/AuthProvider';
import axios from 'axios';

const baseURL = 
process.env.NODE_ENV === 'development'
? 'http://localhost:3001'
: process.env.REACT_APP_SIDEKICK_URL;

const api = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

function ProfileSection({ title, children }) {
    return (
        <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">{title}</h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {children}
            </div>
        </div>
    );
}

function ProfileRow({ label, value, onChange, isEditing, isEditable }) {
    const displayValue = Array.isArray(value) ? value.join(', ') : value;

    return (
        <div className="flex flex-row justify-between items-center my-2">
            <label className="w-1/3 font-semibold text-gray-700">{label}</label>
            {isEditable && isEditing ? (
                <input
                    className="bg-white rounded-md flex-grow p-2 text-right text-gray-600"
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(label.toLowerCase().replace(/\s+/g, '_'), e.target.value)}
                />
            ) : (
                <input
                    className="bg-white rounded-md flex-grow p-2 text-right text-gray-600"
                    type="text"
                    disabled
                    value={displayValue || 'Not specified'}
                />
            )}
        </div>
    );
}

function Profile() {
    const { user, logout, isLoading: authLoading } = useAuth();
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editableUser, setEditableUser] = useState({});
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchUserData = async () => {
            if (!authLoading) {
                const loggedInUserId = parseInt(localStorage.getItem('userId'));
                if (!isNaN(loggedInUserId)) {
                    try {
                        const response = await api.get(`/api/users/${loggedInUserId}`);
                        const userData = response.data;
                        setCurrentUser(userData);
                        setEditableUser(userData);
                    } catch (error) {
                        console.error('Error fetching user data:', error);
                        setError('Failed to load user data');
                    }
                } else {
                    console.warn('No valid userId found in local storage');
                    navigate('/login');
                }
                setLoading(false);
            }
        };
    
        fetchUserData();
    }, [authLoading, navigate]);

    const handleInputChange = (field, value) => {
        const fieldMapping = {
            'company_name': 'business_name',
            'main_objective': 'objective',
            'selected_roles': 'sidekick'
        };
        
        const mappedField = fieldMapping[field] || field;
        
        setEditableUser(prev => ({
            ...prev,
            [mappedField]: value,
        }));
    };

    const handleSave = async () => {
        try {
            setError('');
            const loggedInUserId = parseInt(localStorage.getItem('userId'));
            
            // Add console logs for debugging
            console.log('Sending update request for user:', loggedInUserId);
            
            const payload = {
                id: loggedInUserId,
                name: editableUser.name || '',
                email: editableUser.email || '',
                business_name: editableUser.business_name || '',
                industry: editableUser.industry || '',
                objective: editableUser.objective || '',
                competitors: editableUser.competitors || '',
                target_audience: editableUser.target_audience || '',
                brand_tone: editableUser.brand_tone || '',
                sidekick: editableUser.sidekick || ''
            };
    
            console.log('editableUser:', editableUser);  // Add this line
            console.log('Payload being sent:', payload);  // Add this line
    
            const response = await api.put(
                `/api/users/${loggedInUserId}`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true
                }
            );
    
            if (response.status === 200) {
                setCurrentUser(response.data);
                setEditableUser(response.data);
                setIsEditing(false);
                setError(''); // Clear any existing errors
            }
        } catch (error) {
            console.error('Full error:', error);
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.error('Error response:', error.response.data);
                console.error('Error status:', error.response.status);
                setError(`Update failed: ${error.response.data.message || 'Server error'}`);
            } else if (error.request) {
                // The request was made but no response was received
                console.error('Error request:', error.request);
                setError('No response from server. Please try again.');
            } else {
                // Something happened in setting up the request that triggered an Error
                console.error('Error message:', error.message);
                setError('Failed to send request. Please try again.');
            }
        }
    };

    const handleEditToggle = async () => {
        if (isEditing) {
            await handleSave();
        } else {
            setIsEditing(true);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
            setError('Logout failed');
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
    }

    if (!currentUser) {
        return <div className="flex justify-center items-center min-h-screen">User not found</div>;
    }

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {error && (
                    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}
                
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="px-6 py-8 sm:p-10">
                        <div className="flex justify-between items-center mb-8">
                            <h1 className="text-3xl font-bold text-gray-900">Your Profile</h1>
                            <div className="flex gap-4">
                                <button 
                                    onClick={handleEditToggle} 
                                    className={`px-4 py-2 rounded text-white py-2 px-4 my-2 mx-2 rounded ${isEditing ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'}`}
                                >
                                    {isEditing ? 'Save' : 'Edit'}
                                </button>
                                <LogoutButton onClick={handleLogout} />
                            </div>
                        </div>

                        <ProfileSection title="Personal Information">
                            <ProfileRow label="Name" value={editableUser.name} onChange={handleInputChange} isEditing={isEditing} isEditable={true} />
                            <ProfileRow label="Email" value={editableUser.email} onChange={handleInputChange} isEditing={isEditing} isEditable={false} />
                        </ProfileSection>

                        <ProfileSection title="Business Information">
                            <ProfileRow label="Company Name" value={editableUser.business_name} onChange={handleInputChange} isEditing={isEditing} isEditable={true} />
                            <ProfileRow label="Industry" value={editableUser.industry} onChange={handleInputChange} isEditing={isEditing} isEditable={true} />
                            <ProfileRow label="Main Objective" value={editableUser.objective} onChange={handleInputChange} isEditing={isEditing} isEditable={true} />
                            <ProfileRow label="Competitors" value={editableUser.competitors} onChange={handleInputChange} isEditing={isEditing} isEditable={true} />
                        </ProfileSection>

                        <ProfileSection title="Marketing Information">
                            <ProfileRow label="Target Audience" value={editableUser.target_audience} onChange={handleInputChange} isEditing={isEditing} isEditable={true} />
                            <ProfileRow label="Brand Tone" value={editableUser.brand_tone} onChange={handleInputChange} isEditing={isEditing} isEditable={true} />
                        </ProfileSection>

                        <ProfileSection title="Sidekick Configuration">
                            <ProfileRow label="Selected Roles" value={editableUser.sidekick} onChange={handleInputChange} isEditing={isEditing} isEditable={true} />
                        </ProfileSection>

                        {editableUser.documents && (
                            <ProfileSection title="Uploaded Documents">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-600">
                                        Document uploaded: {editableUser.documents}
                                    </p>
                                </div>
                            </ProfileSection>
                        )}
                    </div>
                </div>

                {process.env.NODE_ENV === 'development' && (
                    <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                        <h2 className="text-lg font-semibold mb-2">Debug Info:</h2>
                        <pre className="text-sm overflow-auto">
                            {JSON.stringify(editableUser, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Profile;